import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    Card,
    CardContent,
    CardMedia,
    CardActionArea,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
   IconButton,
    Grid,
    Container,
    Fade,
    Zoom,
    alpha,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextareaAutosize
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    PlayCircleOutline as PlayIcon,
    AccessTime as TimeIcon,
    School as SchoolIcon,
    VideoLibrary as VideoLibraryIcon,
    Add as AddIcon,
    CloudUpload as UploadIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import tutorialApi from 'src/utils/tutorialApi';
import { showErrorToast, showSuccessToast } from 'src/utils/Toast/SnackbarProvider';
import * as constants from 'src/utils/constants';
import { apiInstance } from 'src/utils/apiAuth';
import UploadVideoDialog from './UploadVideoDialog';
// Construct Base URL
const BASE_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;


// Difficulty configurations
const difficultyConfig = {
    beginner: { label: 'Easy', color: '#43a047', icon: '🟢' },
    intermediate: { label: 'Medium', color: '#fb8c00', icon: '🟡' },
    advanced: { label: 'Advanced', color: '#e53935', icon: '🔴' }
};

export default function Tutorials() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    // State management
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [videoDialogOpen, setVideoDialogOpen] = useState(false);
    const [editingTutorial, setEditingTutorial] = useState(null);
    
    // API data states
    const [tutorials, setTutorials] = useState([]);
    const [categories, setCategories] = useState({ 'All': { color: '#00AB55', icon: '📚' } });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Data loading function
    const loadData = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            // Fetch categories
            const categoriesResponse = await tutorialApi.getCategories();
            console.log('Categories response:', categoriesResponse);
            
            if (categoriesResponse.data?.success) {
                const categoriesData = categoriesResponse.data.data || [];
                
                // Build category config from API data
                const categoryMap = { 'All': { color: '#00AB55', icon: '📚' } };
                categoriesData.forEach(cat => {
                    categoryMap[cat.name] = {
                        color: cat.color || '#1976d2',
                        icon: cat.icon || '📚',
                        id: cat.id
                    };
                });
                setCategories(categoryMap);
            }

            // Fetch tutorials
            const tutorialsResponse = await tutorialApi.getTutorials();
            console.log('Tutorials response:', tutorialsResponse);
            
            if (tutorialsResponse.data?.success) {
                const tutorialsData = tutorialsResponse.data.data || [];
                
                // Transform API data to match frontend structure
                const transformedTutorials = tutorialsData.map(tutorial => {
                    let videoUrl = tutorial.video_url;
                    if (videoUrl && !videoUrl.startsWith('http')) {
                        if (!videoUrl.startsWith('/')) {
                            videoUrl = '/' + videoUrl;
                        }
                        videoUrl = `${BASE_URL}${videoUrl}`;
                    }

                    let thumbnailUrl = tutorial.thumbnail_url;
                    if (thumbnailUrl && !thumbnailUrl.startsWith('http')) {
                        if (!thumbnailUrl.startsWith('/')) {
                            thumbnailUrl = '/' + thumbnailUrl;
                        }
                        thumbnailUrl = `${BASE_URL}${thumbnailUrl}`;
                    }

                    return {
                        id: tutorial.id,
                        title: tutorial.title,
                        description: tutorial.description,
                        category: tutorial.category?.name || 'Uncategorized',
                        videoUrl: videoUrl,
                        thumbnail: thumbnailUrl || `https://via.placeholder.com/400x225/${tutorial.category?.color?.replace('#', '')}/ffffff?text=${encodeURIComponent(tutorial.title)}`,
                        duration: tutorial.duration,
                        difficulty: tutorial.difficulty,
                        tags: tutorial.tags || [],
                        is_featured: tutorial.is_featured
                    };
                });
                
                setTutorials(transformedTutorials);
            } else {
                setError('Failed to load tutorials from server');
            }
        } catch (error) {
            console.error('Error fetching tutorial data:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Failed to load tutorials. Please try again later.';
            setError(errorMessage);
            showErrorToast(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch categories and tutorials on component mount
    useEffect(() => {
        loadData();
    }, []);

    // Check if user is superadmin
    useEffect(() => {
        const checkUserRole = async () => {
            try {
                const response = await apiInstance.get('/users-viewset/?show_details=true&current_user=true&active_role=null');
                console.log('User Role Check Response:', response.data);
                
                if (response.data) {
                    // Check both direct property and nested data property
                    let userData = response.data.data || response.data;
                    
                    // If it's an array (list response), take the first item
                    if (Array.isArray(userData) && userData.length > 0) {
                        userData = userData[0];
                    }
                    
                    const isSuper = userData?.is_superuser || false;
                    
                    console.log('Is Superuser:', isSuper);
                    setIsSuperAdmin(isSuper);
                }
            } catch (error) {
                console.error('Error checking user role:', error);
                setIsSuperAdmin(false);
            }
        };

        checkUserRole();
    }, []);

    // Filter tutorials based on search and category
    const filteredTutorials = useMemo(() => {
        return tutorials.filter(tutorial => {
            const matchesSearch = tutorial.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                tutorial.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                tutorial.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
            
            const matchesCategory = selectedCategory === 'All' || tutorial.category === selectedCategory;
            
            return matchesSearch && matchesCategory;
        });
    }, [searchQuery, selectedCategory, tutorials]);

    const handleVideoClick = async (tutorial) => {
        setSelectedVideo(tutorial);
        setVideoDialogOpen(true);
        
        // Track view
        try {
            await tutorialApi.trackView(tutorial.id, {
                completed: false,
                watch_duration: 0
            });
        } catch (error) {
            console.error('Error tracking view:', error);
        }
    };

    const handleCloseVideo = () => {
        setVideoDialogOpen(false);
        setTimeout(() => setSelectedVideo(null), 300);
    };

    const handleUploadSuccess = () => {
        // Refresh tutorials list and categories after successful upload
        loadData();
    };

    const handleEdit = async (tutorial, event) => {
        event.stopPropagation();
        try {
            // Fetch full tutorial details from API to get category_id
            const response = await tutorialApi.getTutorialById(tutorial.id);
            if (response.data?.success) {
                const fullTutorial = response.data.data;
                setEditingTutorial(fullTutorial);
                setIsUploadDialogOpen(true);
            }
        } catch (error) {
            console.error('Error fetching tutorial details:', error);
            showErrorToast('Failed to load tutorial details');
        }
    };

    const handleDelete = async (tutorialId, event) => {
        event.stopPropagation();
        if (window.confirm('Are you sure you want to delete this tutorial?')) {
            try {
                await tutorialApi.deleteTutorial(tutorialId);
                showSuccessToast('Tutorial deleted successfully');
                loadData();
            } catch (error) {
                console.error('Error deleting tutorial:', error);
                showErrorToast('Failed to delete tutorial');
            }
        }
    };

    const handleCloseUploadDialog = () => {
        setIsUploadDialogOpen(false);
        setEditingTutorial(null);
    };

    return (
        <Box sx={{ 
            minHeight: '100vh',
            background: '#fff',
            pb: 4
        }}>
            <Container maxWidth="xl">
                {/* Header Section */}
                <Box sx={{ 
                    pt: 4, 
                    pb: 3,
                    textAlign: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    mb: 3
                }}>
                    <Fade in timeout={800}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, position: 'relative' }}>
                                <VideoLibraryIcon sx={{ fontSize: 48, color: '#00AB55', mr: 2 }} />
                                <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        fontWeight: 700,
                                        color: '#212B36',
                                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                                    }}
                                >
                                    Tutorials
                                </Typography>
                            </Box>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    color: '#637381',
                                    fontWeight: 400,
                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                }}
                            >
                                Learn how to use every feature of CRM
                            </Typography>
                        </Box>
                    </Fade>
                </Box>

                {/* Search Bar */}
                <Fade in timeout={1000}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search tutorials..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                maxWidth: 600,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: '0 6px 30px rgba(0,0,0,0.2)',
                                    }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Upload Button - Only for Superadmin */}
                        {isSuperAdmin && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setIsUploadDialogOpen(true)}
                                sx={{
                                    height: 56, // Match TextField height
                                    background: 'linear-gradient(135deg, #00AB55 0%, #007B55 100%)',
                                    color: 'white',
                                    px: 3,
                                    borderRadius: 3,
                                    boxShadow: '0 4px 14px 0 rgba(0,171,85,0.39)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #007B55 0%, #005249 100%)',
                                        boxShadow: '0 6px 20px rgba(0,171,85,0.5)',
                                    },
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Upload Video
                            </Button>
                        )}
                    </Box>
                </Fade>

                {/* Category Filters */}
                <Fade in timeout={1200}>
                    <Box sx={{ 
                        mb: 4, 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        px: { xs: 2, sm: 0 }
                    }}>
                        {Object.keys(categories).map((category) => (
                            <Chip
                                key={category}
                                label={`${categories[category].icon} ${category}`}
                                onClick={() => setSelectedCategory(category)}
                                sx={{
                                    backgroundColor: selectedCategory === category 
                                        ? '#00AB55' 
                                        : 'white',
                                    color: selectedCategory === category ? 'white' : '#637381',
                                    fontWeight: selectedCategory === category ? 600 : 500,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    px: { xs: 1, sm: 2 },
                                    py: { xs: 2, sm: 2.5 },
                                    borderRadius: 2,
                                    border: selectedCategory === category ? 'none' : '1px solid #DFE3E8',
                                    boxShadow: selectedCategory === category ? '0 4px 12px rgba(0,171,85,0.24)' : '0 2px 8px rgba(0,0,0,0.08)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        backgroundColor: selectedCategory === category 
                                            ? '#007B55' 
                                            : alpha('#00AB55', 0.08),
                                        transform: 'translateY(-2px)',
                                        boxShadow: selectedCategory === category 
                                            ? '0 6px 16px rgba(0,171,85,0.32)' 
                                            : '0 4px 12px rgba(0,0,0,0.12)',
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Fade>

                {/* Results Count */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: '#637381', fontWeight: 500 }}>
                        {filteredTutorials.length} {filteredTutorials.length === 1 ? 'tutorial' : 'tutorials'} found
                    </Typography>
                </Box>

                {/* Loading State */}
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#00AB55' }} />
                    </Box>
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <Fade in>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <SchoolIcon sx={{ fontSize: 80, color: '#C4CDD5', mb: 2 }} />
                            <Typography variant="h5" sx={{ color: '#212B36', mb: 1 }}>
                                {error}
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#637381' }}>
                                Please refresh the page or try again later
                            </Typography>
                        </Box>
                    </Fade>
                )}

                {/* Tutorial Cards Grid */}
                {!isLoading && !error && (
                    <Grid container spacing={3}>
                    {filteredTutorials.map((tutorial, index) => (
                        <Grid item xs={12} sm={6} md={4} key={tutorial.id}>
                            <Zoom in timeout={300 + index * 100}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        background: 'white',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                                            '& .play-overlay': {
                                                opacity: 1,
                                            },
                                            '& .thumbnail-image': {
                                                transform: 'scale(1.05)',
                                            }
                                        }
                                    }}
                                >
                                    <CardActionArea onClick={() => handleVideoClick(tutorial)}>
                                        <Box sx={{ position: 'relative', paddingTop: '56.25%', overflow: 'hidden' }}>
                                            {/* Edit/Delete Buttons - Only for Superadmin */}
                                            {isSuperAdmin && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    left: 12,
                                                    display: 'flex',
                                                    gap: 1,
                                                    zIndex: 2
                                                }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleEdit(tutorial, e)}
                                                        sx={{
                                                            backgroundColor: alpha('#fff', 0.9),
                                                            '&:hover': {
                                                                backgroundColor: '#fff',
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleDelete(tutorial.id, e)}
                                                        sx={{
                                                            backgroundColor: alpha('#fff', 0.9),
                                                            '&:hover': {
                                                                backgroundColor: '#fff',
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                            <CardMedia
                                                component="img"
                                                image={tutorial.thumbnail}
                                                className="thumbnail-image"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    transition: 'transform 0.3s ease',
                                                }}
                                            />
                                            {/* Category Badge */}
                                            <Chip
                                                label={tutorial.category}
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    right: 12,
                                                    backgroundColor: categories[tutorial.category]?.color || '#1976d2',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                }}
                                            />
                                            {/* Play Button Overlay */}
                                            <Box
                                                className="play-overlay"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: alpha('#000', 0.4),
                                                    opacity: 0,
                                                    transition: 'opacity 0.3s ease',
                                                }}
                                            >
                                                <PlayIcon sx={{ fontSize: 64, color: 'white' }} />
                                            </Box>
                                        </Box>
                                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                            <Typography 
                                                variant="h6" 
                                                gutterBottom 
                                                sx={{ 
                                                    fontWeight: 700,
                                                    fontSize: '1.1rem',
                                                    lineHeight: 1.3,
                                                    mb: 1.5
                                                }}
                                            >
                                                {tutorial.title}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{
                                                    mb: 2,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                {tutorial.description}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                <Chip
                                                    icon={<TimeIcon sx={{ fontSize: 16 }} />}
                                                    label={tutorial.duration}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha('#1976d2', 0.1),
                                                        color: '#1976d2',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                                <Chip
                                                    label={`${difficultyConfig[tutorial.difficulty].icon} ${difficultyConfig[tutorial.difficulty].label}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha(difficultyConfig[tutorial.difficulty].color, 0.1),
                                                        color: difficultyConfig[tutorial.difficulty].color,
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Zoom>
                        </Grid>
                    ))}
                </Grid>
                )}

                {/* No Results */}
                {!isLoading && !error && filteredTutorials.length === 0 && (
                    <Fade in>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <SchoolIcon sx={{ fontSize: 80, color: '#C4CDD5', mb: 2 }} />
                            <Typography variant="h5" sx={{ color: '#212B36', mb: 1 }}>
                                No tutorials found
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#637381' }}>
                                Try adjusting your search or filter criteria
                            </Typography>
                        </Box>
                    </Fade>
                )}
            </Container>

            {/* Video Dialog */}
            <Dialog
                open={videoDialogOpen}
                onClose={handleCloseVideo}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        backgroundColor: '#000',
                    }
                }}
            >
                <Box sx={{ position: 'relative', backgroundColor: '#000' }}>
                    <IconButton
                        onClick={handleCloseVideo}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                            backgroundColor: alpha('#000', 0.5),
                            zIndex: 1,
                            '&:hover': {
                                backgroundColor: alpha('#000', 0.7),
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent sx={{ p: 0, backgroundColor: '#000' }}>
                        {selectedVideo && (
                            <Box>
                                {/* Video Player */}
                                <Box sx={{ 
                                    position: 'relative',
                                    paddingTop: '56.25%',
                                    backgroundColor: '#000'
                                }}>
                                    <video
                                        controls
                                        controlsList="nodownload"
                                        playsInline
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 'none'
                                        }}
                                    >
                                        <source src={selectedVideo.videoUrl} type="video/mp4" />
                                        Your browser does not support the video element.
                                    </video>
                                </Box>
                                {/* Video Info */}
                                <Box sx={{ p: 3, backgroundColor: '#1a1a1a' }}>
                                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                        {selectedVideo.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: alpha('#fff', 0.8), mb: 2 }}>
                                        {selectedVideo.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`${categories[selectedVideo.category]?.icon} ${selectedVideo.category}`}
                                            sx={{
                                                backgroundColor: categories[selectedVideo.category]?.color,
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                        <Chip
                                            icon={<TimeIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                                            label={selectedVideo.duration}
                                            sx={{
                                                backgroundColor: alpha('#fff', 0.2),
                                                color: 'white',
                                                fontWeight: 500
                                            }}
                                        />
                                        <Chip
                                            label={`${difficultyConfig[selectedVideo.difficulty].icon} ${difficultyConfig[selectedVideo.difficulty].label}`}
                                            sx={{
                                                backgroundColor: difficultyConfig[selectedVideo.difficulty].color,
                                                color: 'white',
                                                fontWeight: 500
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Box>
            </Dialog>

            {/* Upload Video Dialog - Only for Superadmin */}
            <UploadVideoDialog
                open={isUploadDialogOpen}
                onClose={handleCloseUploadDialog}
                categoriesData={categories}
                onUploadSuccess={handleUploadSuccess}
                editingTutorial={editingTutorial}
            />
        </Box>
    );
}
